<template>
  <q-dialog ref="dialogRef" persistent @hide="onDialogHide">
    <q-card
      :style="{
        minWidth: '600px',
      }"
      class="relative-position elevation-1"
    >
      <q-card-section
        class="flex justify-between items-center q-px-2x-md q-py-xl"
      >
        <span class="text-lg text-neutral text-weight-medium">
          {{ status == "approved" ? "Approve" : "Reject" }} CR Report
        </span>

        <PBtn flat dense round size="sm" @click="() => onDialogCancel()">
          <CloseIcon class="icon" />
        </PBtn>
      </q-card-section>

      <q-separator></q-separator>

      <q-card-section
        class="rounded-borders text-neutral-90 text-center q-px-2x-md q-py-xl"
      >
        <q-form @submit="onSubmit">
          <PSelectInput
            v-model="formData.approvers"
            label="Select Approvers"
            placeholder="Select name"
            help="Select someone who is responsible for this approval"
            help-classes="text-xs text-neutral-70 q-mb-xs flex"
            :options="formattedApproversList"
            :options-loading="unref(kamFilter.loading)"
            option-label="name"
            option-value="_id"
            required
            multiple
            use-chips
            @virtual-scroll="kamFilter.onScroll"
            @filter="kamFilter.onFilter"
            @filter-abort="kamFilter.onAbortFilter"
          />
          <div>
            <div class="row q-col-gutter-x-xxl q-mt-sm">
              <div class="col-5">
                <div class="text-sm text-neutral q-mt-xs text-left">
                  Eligible for Credit / Advance
                </div>
              </div>
              <div class="col-7">
                <PSelectInput
                  v-model="computedExposureEligibility"
                  :options="unref(eligibilityOptions)"
                  option-label="name"
                  option-value="code"
                  :clearable="false"
                  disable
                />
              </div>
            </div>
            <div
              v-if="status == 'approved'"
              class="row q-col-gutter-x-xxl q-mt-sm"
            >
              <div class="col-5">
                <div class="text-sm text-neutral q-mt-xs text-left">
                  Exposure Limit
                </div>
              </div>
              <div class="col-7">
                <PInput
                  v-model.number="formData.credit_limit"
                  required
                  type="number"
                  disable
                  class="prepend-bg"
                >
                  <template #prepend>
                    <span class="text-sm">₹</span>
                  </template>
                </PInput>
              </div>
            </div>
          </div>
          <div class="q-mt-xxl">
            <PInput
              v-model="formData.remarks"
              type="textarea"
              label="Remarks"
              placeholder="Enter comment"
              required
            />
          </div>

          <div class="flex justify-end q-mt-xl">
            <PBtn
              :fluid="false"
              :loading="isLoading"
              label="Submit"
              type="submit"
            />
          </div>
        </q-form>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { useDialogPluginComponent } from "quasar";

import CloseIcon from "assets/icons/close.svg";
import { unref } from "vue";

const { putUserCreditReportStatus } = useUserStore();

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } =
  useDialogPluginComponent();

const props = defineProps({
  status: {
    type: String,
    default: "",
  },
  creditLimit: {
    type: String,
    default: "",
  },
  approversList: {
    type: Array,
    default: () => [],
  },
});

const eligibilityOptions = ref([
  {
    name: "Eligible",
    code: "E",
    value: true,
  },
  {
    name: "Not Eligible",
    code: "NE",
    value: false,
  },
  {
    name: "Inconclusive",
    code: "NC",
    value: false,
  },
]);

const isLoading = ref(false);

const formData = ref({
  approvers: null,
  remarks: "",
  credit_limit: unref(props).creditLimit,
  credit_eligibility:
    unref(props).status == "approved"
      ? unref(eligibilityOptions)[0]
      : unref(eligibilityOptions)[1],
});

const kamFilter = useFilter({
  entity: "user",
  options: {
    params: {
      roles: "kam",
      isActive: 1,
    },
  },
});

const computedExposureEligibility = computed({
  get() {
    if (unref(formData).credit_eligibility) {
      return unref(formData).credit_eligibility;
    }

    return null;
  },
  set(value) {
    unref(formData).credit_eligibility = unref(eligibilityOptions).find(
      (opt) => opt.code == value,
    );
  },
});

const onSubmit = async () => {
  try {
    isLoading.value = true;

    await putUserCreditReportStatus({
      stage_cd:
        unref(props).status == "approved"
          ? "approve_credit_report"
          : "reject_credit_report",
      approvers: unref(formData).approvers,
      remarks: unref(formData).remarks,
      status_cd: unref(props).status,
      credit_limit: unref(formData).credit_limit,
      credit_eligibility: unref(formData).credit_eligibility,
    });

    onDialogOK();
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const formattedApproversList = computed(() => {
  return unref(props.approversList).map((item) => ({
    ...item,
    _id: item._id,
    name: item.name,
  }));
});
</script>
